import { createGlobalStyle } from 'styled-components';

/* ============================================================
   Instructor foundation — the ONLY global stylesheet for the
   panel (replaces the old CSS.css). Scoped to .instructor-app-shell
   so it cannot interfere with the rest of the site.

   Theme-driven via the styled-components ThemeProvider (light/dark),
   so toggling the theme re-skins the whole shell. Carries only the
   app-shell grid (with the collapse transition), base reset, body
   typography, focus ring, selection, and tabular numbers. All
   component styling lives in per-component styled-components.

   The grid's left column is var(--sidebar-w); MyRoutes sets it
   inline (248px expanded / 76px collapsed) to drive the sidebar
   collapse, matching the reference [data-sidebar="icon"] behavior.
   ============================================================ */
const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Hanken+Grotesk:wght@400;500;600;700;800&family=Outfit:wght@400;500;600;700;800&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Sora:wght@400;500;600;700;800&family=Libre+Franklin:wght@400;500;600;700;800&family=Space+Grotesk:wght@400;500;600;700&display=swap');

  html, body { margin: 0; height: 100%; }

  @media (prefers-reduced-motion: no-preference) {
    ::view-transition-old(root),
    ::view-transition-new(root) {
      animation: none;
      mix-blend-mode: normal;
    }

    ::view-transition-old(root) { z-index: 1; }
    ::view-transition-new(root) { z-index: 9999; }
  }

  .instructor-app-shell {
    display: grid;
    grid-template-columns: var(--sidebar-w, 248px) minmax(0, 1fr);
    height: 100vh;
    overflow: hidden;
    transition: grid-template-columns .28s cubic-bezier(.4, 0, .2, 1);

    color-scheme: ${({ theme }) => theme.colorScheme};
    font-family: ${({ theme }) => theme.fontBody};
    color: ${({ theme }) => theme.text};
    background: ${({ theme }) => theme.bg};
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
  }

  @media (max-width: 768px) {
    .instructor-app-shell { grid-template-columns: 0 minmax(0, 1fr); }
  }

  .instructor-app-shell *,
  .instructor-app-shell *::before,
  .instructor-app-shell *::after { box-sizing: border-box; }

  /* Global App.css has \`* { font-family: 'Sora' }\` which sets the font on
     every element directly (overriding inheritance). Re-assert the panel font
     on the shell's subtree so the whole Instructor panel uses Hanken Grotesk.
     Scoped to .instructor-app-shell so the marketing site keeps its font. */
  .instructor-app-shell,
  .instructor-app-shell * { font-family: ${({ theme }) => theme.fontBody}; }

  /* Inherit only the font FAMILY here. Do NOT reset \`color\` or use the
     \`font\` shorthand: at .instructor-app-shell-button specificity (0,1,1)
     either would override every styled button's own color/size (0,1,0) —
     that's why button text stayed dark / the wrong size. Components set
     their own color and size; inputs set their own color too. */
  .instructor-app-shell button,
  .instructor-app-shell input,
  .instructor-app-shell select,
  .instructor-app-shell textarea { font-family: inherit; }

  .instructor-app-shell :focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 2px;
  }
  .instructor-app-shell ::selection {
    background: ${({ theme }) => `color-mix(in oklch, ${theme.accent} 28%, transparent)`};
  }

  /* numbers: tabular everywhere */
  .instructor-app-shell .tnum {
    font-variant-numeric: tabular-nums;
    font-feature-settings: "tnum" 1;
  }
`;

export default GlobalStyle;
